/**
 * Coming back to a map (Sam, 2026-10-31): soon and where you were, it is exactly the map you left - the camera, the
 * rings, following you, the Go you were on. After 15 minutes, or once you have moved 50 m, it is the default 3D view
 * on you instead: the old view would be somewhere you no longer are.
 *
 * Kept for the app session, per map (FestivalMap3D keeps the view, MapsPage the Go target). Each side stores its own
 * part; `fresh` is the one rule both read.
 */
import { haversineMeters } from '../proximity/engine/geodesy';

export const RETURN_MS = 15 * 60 * 1000;
export const RETURN_M = 50;

const left = new Map(); // map key -> { at, self, ...parts }

/**
 * You leaving `key`: `self` is where you were ({lat, lng}, or null off the map), `parts` what to put back. The map
 * passes `self`; MapsPage passes undefined (its Go target only), which leaves the map's time and place as they are.
 */
export function rememberLeave(key, self, parts) {
  if (!key) return;
  const was = left.get(key);
  const place =
    self === undefined
      ? {}
      : { at: Date.now(), self: self?.lat != null ? { lat: self.lat, lng: self.lng } : null };
  left.set(key, { ...was, ...parts, ...place });
}

/** What you left `key` with, or null when there is none or it is stale (time, or you moved). */
export function leftAsItWas(key, self) {
  const was = key ? left.get(key) : null;
  if (!was) return null;
  return fresh(was, self) ? was : null;
}

/** Stale views are dropped the first time they are found stale, so a later open does not revive them. */
export function forgetLeave(key) {
  if (key) left.delete(key);
}

function fresh(was, self) {
  if (!(Date.now() - was.at < RETURN_MS)) return false; // no `at`: the map never recorded leaving
  // Moved: both places known and 50 m apart (home and then the venue is kilometres). Where you are is often not known
  // yet as the map opens; then time alone decides.
  if (was.self?.lat == null || self?.lat == null) return true;
  return haversineMeters(was.self.lat, was.self.lng, self.lat, self.lng) < RETURN_M;
}

/** You, when you are on the venue (+ ~250 m, the peers' clamp margin); else null. `venue` is [[w, s], [e, n]]. */
export function onVenue(pos, venue) {
  const M = 0.0025;
  return pos?.lat != null &&
    venue &&
    pos.lng >= venue[0][0] - M &&
    pos.lng <= venue[1][0] + M &&
    pos.lat >= venue[0][1] - M &&
    pos.lat <= venue[1][1] + M
    ? pos
    : null;
}
